/**
 * Source-document review PDF preview, page nav, and review-decision actions.
 */
import { formatInteger } from "./format.js";
import { escapeAttr, escapeHtml, labelize } from "./dashboard-html.js";
import { authenticatedFetch, DashboardAuthRequiredError } from "./dashboard-auth.js";
import type { DashboardActionHost } from "./dashboard-referenced-deed-actions.js";
import { leadFactoryOcrRunUrl } from "./dashboard-urls.js";
import type { SourceDocumentReviewFocus } from "./dashboard-lead-factory-types.js";

type ShowSourceDocumentReview = (
  sourceDocumentId: string,
  statusElement: HTMLElement,
  focus?: SourceDocumentReviewFocus
) => Promise<void>;

const leadFactoryReviewOutcomes = [
  "classification_confirmed",
  "extraction_confirmed",
  "evidence_confirmed",
  "needs_rework",
  "duplicate_reviewed",
  "not_relevant",
  "invalid_pdf",
  "other_typed_review"
] as const;

function cssEscape(value: string): string {
  if (globalThis.CSS?.escape) return globalThis.CSS.escape(value);
  return value.replaceAll("\\", "\\\\").replaceAll('"', '\\"');
}

let currentInlinePreviewObjectUrl: string | null = null;
let currentInlinePreviewSourceDocumentId: string | null = null;
let currentInlinePreviewPage = 1;
export function bindSourceDocumentReviewActions(
  statusElement: HTMLElement,
  showReview: ShowSourceDocumentReview
): void {
  document.querySelectorAll<HTMLButtonElement>("[data-source-document-review]").forEach((button) => {
    button.addEventListener("click", () => {
      const sourceDocumentId = button.dataset.sourceDocumentReview;
      if (!sourceDocumentId) return;
      void showReview(sourceDocumentId, statusElement);
    });
  });
}

export async function hydrateSourceDocumentInlinePreview(
  sourceDocumentId: string,
  statusElement: HTMLElement,
  actions: DashboardActionHost,
  initialPage = 1
): Promise<void> {
  const frameShell = document.querySelector<HTMLElement>(
    `[data-source-document-inline-preview="${cssEscape(sourceDocumentId)}"]`
  );
  if (!frameShell) return;
  revokeInlinePreviewObjectUrl();
  try {
    const response = await authenticatedFetch(
      `/dashboard/lead-factory/source-documents/${encodeURIComponent(sourceDocumentId)}/artifact`
    );
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error ?? `PDF request failed with ${response.status}`);
    }
    currentInlinePreviewObjectUrl = URL.createObjectURL(await response.blob());
    currentInlinePreviewSourceDocumentId = sourceDocumentId;
    const page = Number.isFinite(initialPage) ? Math.max(1, Math.floor(initialPage)) : 1;
    currentInlinePreviewPage = page;
    frameShell.innerHTML = `
      <iframe
        title="Source document PDF"
        src="${escapeAttr(pdfPreviewUrlForPage(page))}"
      ></iframe>
    `;
    const input = document.querySelector<HTMLInputElement>(`[data-pdf-page-input="${cssEscape(sourceDocumentId)}"]`);
    if (input) input.value = String(page);
    statusElement.textContent = `${sourceDocumentId}: source document review and PDF page ${formatInteger(page)} loaded.`;
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    frameShell.innerHTML = `<p class="error-state">${escapeHtml(error instanceof Error ? error.message : "PDF preview failed.")}</p>`;
  }
}

export function bindSourceDocumentPdfNavigationActions(statusElement: HTMLElement): void {
  document.querySelectorAll<HTMLButtonElement>("[data-pdf-page-step]").forEach((button) => {
    button.addEventListener("click", () => {
      const sourceDocumentId = button.dataset.sourceDocumentId;
      if (!sourceDocumentId) return;
      const step = Number(button.dataset.pdfPageStep ?? 0);
      const nextPage = Math.max(1, currentInlinePreviewPage + (Number.isFinite(step) ? step : 0));
      setInlinePreviewPage(sourceDocumentId, nextPage, statusElement);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-pdf-page-apply]").forEach((button) => {
    button.addEventListener("click", () => {
      const sourceDocumentId = button.dataset.pdfPageApply;
      if (!sourceDocumentId) return;
      const input = document.querySelector<HTMLInputElement>(`[data-pdf-page-input="${cssEscape(sourceDocumentId)}"]`);
      const requestedPage = Number(input?.value ?? 1);
      setInlinePreviewPage(sourceDocumentId, requestedPage, statusElement);
    });
  });
  document.querySelectorAll<HTMLInputElement>("[data-pdf-page-input]").forEach((input) => {
    input.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      const sourceDocumentId = input.dataset.pdfPageInput;
      if (!sourceDocumentId) return;
      setInlinePreviewPage(sourceDocumentId, Number(input.value), statusElement);
    });
  });
}

export function setInlinePreviewPage(sourceDocumentId: string, requestedPage: number, statusElement: HTMLElement): void {
  if (!currentInlinePreviewObjectUrl || currentInlinePreviewSourceDocumentId !== sourceDocumentId) {
    statusElement.textContent = `${sourceDocumentId}: PDF is still loading.`;
    return;
  }
  const page = Number.isFinite(requestedPage) ? Math.max(1, Math.floor(requestedPage)) : 1;
  currentInlinePreviewPage = page;
  const input = document.querySelector<HTMLInputElement>(`[data-pdf-page-input="${cssEscape(sourceDocumentId)}"]`);
  if (input) input.value = String(page);
  const iframe = document.querySelector<HTMLIFrameElement>(
    `[data-source-document-inline-preview="${cssEscape(sourceDocumentId)}"] iframe`
  );
  if (iframe) iframe.src = pdfPreviewUrlForPage(page);
  statusElement.textContent = `${sourceDocumentId}: PDF page ${page}.`;
}

export function pdfPreviewUrlForPage(page: number): string {
  const objectUrl = currentInlinePreviewObjectUrl ?? "";
  return `${objectUrl}#page=${Math.max(1, page)}&zoom=page-width`;
}

export function bindEvidenceJumpActions(): void {
  document.querySelectorAll<HTMLButtonElement>("[data-evidence-jump]").forEach((button) => {
    button.addEventListener("click", () => {
      const targetSelector = button.dataset.evidenceJump;
      if (!targetSelector) return;
      const target = document.querySelector<HTMLElement>(targetSelector);
      if (!target) return;
      target.scrollIntoView({ block: "center", behavior: "smooth" });
      target.classList.add("evidence-focus");
      window.setTimeout(() => target.classList.remove("evidence-focus"), 1600);
    });
  });
}

export function bindSourceDocumentReviewDecisionActions(
  sourceDocumentId: string,
  statusElement: HTMLElement,
  showReview: ShowSourceDocumentReview,
  actions: DashboardActionHost
): void {
  document.querySelectorAll<HTMLFormElement>("[data-source-review-decision-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const outcome = form.querySelector<HTMLSelectElement>("[data-source-review-outcome]")?.value ?? "";
      const reason = form.querySelector<HTMLInputElement>("[data-source-review-reason]")?.value ?? "";
      const confirmed = form.querySelector<HTMLInputElement>("[data-source-review-confirm]")?.checked ?? false;
      const taskIds = selectedSourceReviewTaskIds();
      if (taskIds.length === 0) {
        statusElement.textContent = "Select at least one source-document review task before completing a decision.";
        return;
      }
      if (!confirmed) {
        statusElement.textContent = "Confirm the source-document review decision before submitting.";
        return;
      }
      void submitSourceDocumentReviewDecision(sourceDocumentId, taskIds, outcome, reason, statusElement, showReview, actions);
    });
  });
  document.querySelectorAll<HTMLInputElement>("[data-source-review-task-checkbox]").forEach((checkbox) => {
    checkbox.addEventListener("change", updateSourceReviewSelectedCount);
  });
  updateSourceReviewSelectedCount();
}

export function selectedSourceReviewTaskIds(): string[] {
  return Array.from(document.querySelectorAll<HTMLInputElement>("[data-source-review-task-checkbox]:checked"))
    .map((checkbox) => checkbox.value.trim())
    .filter(Boolean);
}

export function bindSourceReviewOcrRetryActions(
  sourceDocumentId: string,
  statusElement: HTMLElement,
  showReview: ShowSourceDocumentReview,
  actions: DashboardActionHost
): void {
  document.querySelectorAll<HTMLButtonElement>("[data-source-review-ocr-retry]").forEach((button) => {
    button.addEventListener("click", () => {
      const targetSourceDocumentId = button.dataset.sourceReviewOcrRetry;
      if (!targetSourceDocumentId || targetSourceDocumentId !== sourceDocumentId) return;
      void submitSourceReviewOcrRetry(targetSourceDocumentId, statusElement, showReview, actions);
    });
  });
}

export function updateSourceReviewSelectedCount(): void {
  const selected = selectedSourceReviewTaskIds().length;
  const total = document.querySelectorAll<HTMLInputElement>("[data-source-review-task-checkbox]").length;
  const target = document.querySelector<HTMLElement>("[data-source-review-selected-count]");
  if (!target) return;
  target.textContent = `${formatInteger(selected)} selected of ${formatInteger(total)} active task${total === 1 ? "" : "s"}`;
}

export async function submitSourceReviewOcrRetry(
  sourceDocumentId: string,
  statusElement: HTMLElement,
  showReview: ShowSourceDocumentReview,
  actions: DashboardActionHost
): Promise<void> {
  statusElement.textContent = `${sourceDocumentId}: retrying targeted OCR...`;
  try {
    const response = await authenticatedFetch(leadFactoryOcrRunUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        sourceDocumentId,
        limit: 1,
        retryCompletedWithoutResult: true
      })
    });
    const body = (await response.json()) as {
      error?: string;
      processedTasks?: number;
      failuresRecorded?: number;
      failureCounts?: Record<string, number>;
    };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    const failureSummary = Object.entries(body.failureCounts ?? {})
      .map(([failureClass, count]) => `${failureClass}: ${formatInteger(count)}`)
      .join(", ");
    statusElement.textContent = `${sourceDocumentId}: targeted OCR retry processed ${formatInteger(body.processedTasks ?? 0)} task(s); ${formatInteger(body.failuresRecorded ?? 0)} failure(s)${failureSummary ? ` (${failureSummary})` : ""}. Refreshing review...`;
    window.setTimeout(() => void showReview(sourceDocumentId, statusElement), 700);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Targeted OCR retry failed.";
  }
}

export async function submitSourceDocumentReviewDecision(
  sourceDocumentId: string,
  taskIds: string[],
  outcome: string,
  reasonValue: string,
  statusElement: HTMLElement,
  showReview: ShowSourceDocumentReview,
  actions: DashboardActionHost
): Promise<void> {
  if (!leadFactoryReviewOutcomes.includes(outcome as (typeof leadFactoryReviewOutcomes)[number])) {
    statusElement.textContent = `Review decision cancelled; choose one of ${leadFactoryReviewOutcomes.join(", ")}.`;
    return;
  }
  const reason = reasonValue.trim();
  if (!reason) {
    statusElement.textContent = "Review decision cancelled; no reason entered.";
    return;
  }
  statusElement.textContent = `${sourceDocumentId}: completing ${taskIds.length} review task(s) as ${labelize(outcome)}...`;
  try {
    const response = await authenticatedFetch("/dashboard/lead-factory/review-queue/complete", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        taskIds,
        outcome,
        reason,
        note: `Source document review decision for ${sourceDocumentId}: ${outcome}.`
      })
    });
    const body = (await response.json()) as { error?: string; completedCount?: number; skippedCount?: number; outcome?: string };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${sourceDocumentId}: completed ${formatInteger(body.completedCount ?? 0)} review task(s) as ${body.outcome ?? outcome}; skipped ${formatInteger(body.skippedCount ?? 0)}. Refreshing review...`;
    window.setTimeout(() => void showReview(sourceDocumentId, statusElement), 700);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Source-document review decision failed.";
  }
}

export function revokeInlinePreviewObjectUrl(): void {
  if (!currentInlinePreviewObjectUrl) return;
  URL.revokeObjectURL(currentInlinePreviewObjectUrl);
  currentInlinePreviewObjectUrl = null;
  currentInlinePreviewSourceDocumentId = null;
  currentInlinePreviewPage = 1;
}

